/**
 * O que faz: mostra o criativo do anúncio Meta colado na mensagem que veio do clique.
 * Onde usa: bolha do ChatWindow quando existe atribuição CTWA para aquela mensagem.
 * Conexões: LeadAdPreview com URL MinIO assinada e fallbacks legados da atribuição.
 * Tabelas: nenhuma leitura direta.
 * Data: 11/09/2026
 */
import { useEffect, useState } from 'react';
import { ExternalLink, Instagram, Megaphone } from 'lucide-react';
import type { LeadAdPreview } from '@/hooks/useLeadAdPreviews';

interface ChatAdPreviewCardProps {
  anuncio: LeadAdPreview;
}

const urlPareceImagemDireta = (url: string | null): boolean => {
  if (!url) return false;
  return /\.(jpg|jpeg|png|webp|gif)(\?|#|$)/i.test(url)
    || /fbcdn\.net|scontent/i.test(url);
};

const rotuloPlataforma = (plataforma: string | null): string => {
  const valor = (plataforma || '').toLowerCase();
  if (valor.includes('instagram')) return 'Instagram';
  if (valor.includes('facebook')) return 'Facebook';
  if (valor.includes('whatsapp')) return 'WhatsApp';
  return 'Meta Ads';
};

export const ChatAdPreviewCard = ({ anuncio }: ChatAdPreviewCardProps) => {
  const fontesMiniatura = [
    anuncio.url_miniatura_assinada,
    anuncio.url_miniatura,
    urlPareceImagemDireta(anuncio.url_midia) ? anuncio.url_midia : null,
  ].filter((url, indice, fontes): url is string => Boolean(url) && fontes.indexOf(url) === indice);
  const chaveFontes = fontesMiniatura.join('|');
  const [indiceFonte, setIndiceFonte] = useState(0);

  useEffect(() => {
    // Reinicia a cadeia quando o React reutiliza o cartão para outra atribuição ou URL renovada.
    setIndiceFonte(0);
  }, [chaveFontes]);

  const plataforma = rotuloPlataforma(anuncio.plataforma);
  const titulo = anuncio.titulo_anuncio || 'Anúncio';
  const copy = anuncio.copy_anuncio;
  const urlDestino = anuncio.url_anuncio || anuncio.url_midia;
  const miniaturaAtual = fontesMiniatura[indiceFonte] ?? null;

  const conteudo = (
    <div className="flex gap-3">
      <div className="relative h-24 w-24 shrink-0 overflow-hidden rounded-lg bg-secondary">
        {miniaturaAtual ? (
          <img
            src={miniaturaAtual}
            alt={`Criativo do anúncio ${titulo}`}
            className="h-full w-full object-cover"
            referrerPolicy="no-referrer"
            onError={() => {
              // Tenta a próxima origem sem esconder o restante do cartão do anúncio.
              setIndiceFonte((indiceAtual) => indiceAtual + 1);
            }}
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-[#0f2e1d] to-[#2c6e49] text-[#d8ff3c]">
            {plataforma === 'Instagram' ? (
              <Instagram className="h-7 w-7" aria-hidden="true" />
            ) : (
              <Megaphone className="h-7 w-7" aria-hidden="true" />
            )}
          </div>
        )}
      </div>

      <div className="min-w-0 flex-1">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-accent-foreground">
          Veio de um anúncio · {plataforma}
        </p>
        <p className="mt-0.5 truncate text-sm font-semibold text-foreground">{titulo}</p>
        {copy && (
          <p className="mt-0.5 line-clamp-2 text-xs leading-snug text-muted-foreground">{copy}</p>
        )}
        {urlDestino && (
          <p className="mt-1 inline-flex items-center gap-1 text-[11px] font-medium text-accent-foreground">
            Abrir anúncio
            <ExternalLink className="h-3 w-3" aria-hidden="true" />
          </p>
        )}
      </div>
    </div>
  );

  if (!urlDestino) {
    return (
      <div className="mb-2 rounded-xl border border-primary/40 bg-accent/70 p-2.5">
        {conteudo}
      </div>
    );
  }

  return (
    <a
      href={urlDestino}
      target="_blank"
      rel="noopener noreferrer"
      className="mb-2 block rounded-xl border border-primary/40 bg-accent/70 p-2.5 transition-colors hover:border-primary/40 hover:bg-accent"
    >
      {conteudo}
    </a>
  );
};
